import axios, { AxiosError } from 'axios'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import { ElMessage } from 'element-plus'
import type { ApiResponse } from '@/types/api'
import { clearAuth, getToken } from '@/utils/auth'

const SUCCESS_CODES: Array<string | number> = [0, 200, '0', '200']

export interface PlatformRequestConfig<D = unknown> extends AxiosRequestConfig<D> {
  skipErrorMessage?: boolean
}

const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
})

function showError(message: string, config?: PlatformRequestConfig): void {
  if (!config?.skipErrorMessage) {
    ElMessage.error(message)
  }
}

function getErrorMessage(error: AxiosError<ApiResponse>): string {
  const status = error.response?.status
  const responseData = error.response?.data
  const serverMessage = responseData?.message || responseData?.msg

  if (serverMessage) return serverMessage
  if (error.code === 'ECONNABORTED') return '请求超时，请稍后重试'
  if (status === 401) return '登录状态已失效，请重新登录'
  if (status === 403) return '当前账号没有操作权限'
  if (status === 404) return '请求的接口不存在'
  if (status !== undefined && status >= 500) return '服务暂时不可用，请稍后重试'
  if (!error.response) return '网络连接异常，请检查网络'

  return error.message || '请求失败，请稍后重试'
}

function redirectToLogin(): void {
  if (window.location.pathname === '/login') return

  const redirect = `${window.location.pathname}${window.location.search}`
  window.location.replace(`/login?redirect=${encodeURIComponent(redirect)}`)
}

service.interceptors.request.use(
  (config) => {
    const token = getToken()

    if (token && config.headers.isToken !== false) {
      config.headers.Authorization = `Bearer ${token}`
    }

    config.headers.delete(['isToken', 'repeatSubmit'])
    return config
  },
  (error: unknown) => Promise.reject(error)
)

service.interceptors.response.use(
  (response) => {
    const payload = response.data as ApiResponse | unknown
    const hasBusinessCode = typeof payload === 'object'
      && payload !== null
      && Object.prototype.hasOwnProperty.call(payload, 'code')

    if (!hasBusinessCode) {
      return payload as unknown as AxiosResponse
    }

    const businessPayload = payload as ApiResponse

    if ([401, '401'].includes(businessPayload.code as string | number)) {
      const message = businessPayload.message || businessPayload.msg || '登录状态已失效，请重新登录'
      const error = new AxiosError<ApiResponse>(
        message,
        undefined,
        response.config,
        response.request,
        response as AxiosResponse<ApiResponse>
      )
      clearAuth()
      redirectToLogin()
      showError(message, response.config as PlatformRequestConfig)
      return Promise.reject(error)
    }

    if (!SUCCESS_CODES.includes(businessPayload.code as string | number)) {
      const message = businessPayload.message || businessPayload.msg || '业务处理失败'
      const error = new AxiosError<ApiResponse>(
        message,
        undefined,
        response.config,
        response.request,
        response as AxiosResponse<ApiResponse>
      )
      showError(message, response.config as PlatformRequestConfig)
      return Promise.reject(error)
    }

    return businessPayload as unknown as AxiosResponse
  },
  (error: AxiosError<ApiResponse>) => {
    const message = getErrorMessage(error)

    if (error.response?.status === 401) {
      clearAuth()
      redirectToLogin()
    }

    showError(message, error.config as PlatformRequestConfig | undefined)
    return Promise.reject(error)
  }
)

export default function request<T = ApiResponse, D = unknown>(
  config: PlatformRequestConfig<D>
): Promise<T> {
  return service.request<unknown, T, D>(config)
}
